import { useTranslation } from 'react-i18next';
import CustomCarousel, { Item } from './CustomCarousel';
import useScreenType from '../../hooks/utility/useScreenType';
import classnames from 'classnames';
import { FC, ReactElement, useMemo } from 'react';
import collect from 'collect.js';
import DiscountCard from '../discount/DiscountCard';
import useGoogleReview from '../../hooks/google-review/useGoogleReview';
import GoogleIcon from '../../assets/logo/GoogleIcon';
import PlusCircleIcon, { TYPE } from '../../assets/logo/PlusCircleIcon';
import useTheme from '../../hooks/ui/useTheme';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import { Modals, Routes } from '../../modules/mobile/exports/Enums';
import {
  PROMO_CODE_DISCOUNT_TYPES,
  PROMO_CODE_TYPES,
  REDUX_STORE_SLICES,
  SCREENS,
} from '../../services/exports/Constants';
import useRouter from '../../hooks/navigation/useRouter';
import { CouponDetailsInterface } from '../../services/exports/Interfaces';
import useAppMethods from '../../hooks/utility/useAppMethods';
import useStore from '../../hooks/store/useStore';
import usePromoCode from '../../hooks/order/usePromoCode';
import useAuth from '../../hooks/global/useAuth';
import useModalRouter from '../../modules/mobile/common/hooks/useModalRouter';
interface Props {
  className?: string;
  itemsClassName?: string;
  dealClassName?: string;
}

export enum DEAL_TYPES {
  GOOGLE_REVIEW_PROGRAM = 'google_review_program',
  SMART_PRICING_RULE = 'smart_pricing_rule',
}

const DealsCarousel = (props: Props): ReactElement => {
  const { className, dealClassName, itemsClassName } = props;

  const { t } = useTranslation(null, { keyPrefix: 'Components:Carousel:DealsCarousel' });
  const { company } = useStore<REDUX_STORE_SLICES.INITIAL_DATA>(REDUX_STORE_SLICES.INITIAL_DATA);
  const theme = useTheme();
  const { navigate } = useRouter();
  const { isDesktop } = useScreenType();
  const { formatCurrency, toggleLoginModal } = useAppMethods();
  const { openModal } = useModalRouter();
  const { isLoggedIn } = useAuth();
  const { appliedPromoCode, showLoginPrompt, removePromoCode, applyPromoCode } = usePromoCode();
  const { hasGooglePromotion, hasUploadedGoogleReview, googlePromotion } = useGoogleReview({ autoLoad: true });
  const { isMobileApp } = useMobileApp();

  const onGoogleReviewProgramClick = (): void => {
    navigate({
      mobile: {
        path: Routes.UploadGoogleReview,
      },
      web: {
        to: SCREENS.UPLOAD_GOOGLE_REVIEW_SCREEN,
        options: {
          state: { prevPage: location.pathname },
        },
      },
    });
  };

  const getDiscount = (coupon: CouponDetailsInterface): string | number => {
    switch (coupon?.discount_type) {
      case PROMO_CODE_DISCOUNT_TYPES.PERCENTAGE:
        return `${coupon?.discount}%`;
      case PROMO_CODE_DISCOUNT_TYPES.FIXED_AMOUNT:
        return formatCurrency(coupon?.discount);
    }
  };

  const getPromoCodeDescription = (coupon: CouponDetailsInterface): JSX.Element => {
    if (coupon.type === PROMO_CODE_TYPES.MULTI_PROMO_CODE) {
      return t(`discounts.description.${PROMO_CODE_TYPES.MULTI_PROMO_CODE}`, {
        discount: getDiscount(coupon),
        count: coupon?.components.length,
        code: coupon.code,
      });
    }
    return t(`discounts.description.${PROMO_CODE_TYPES.ONE_TIME_PROMO_CODE}`, {
      discount: getDiscount(coupon),
      code: coupon.code,
    });
  };

  const AddButton = (): ReactElement => {
    return (
      <div className="flex cursor-pointer">
        <PlusCircleIcon type={TYPE.outline} color={theme.color.brand.primary} className="h-4 w-4 my-auto" />
        <p className="my-auto ml-2xs text-brand-primary text-sm font-semibold">{t(`deals.buttons.add`)}</p>
      </div>
    );
  };

  const RemoveButton = (): ReactElement => {
    return (
      <div className="flex cursor-pointer">
        <p className="my-auto ml-2xs text-brand-primary text-sm font-semibold">{t(`deals.buttons.remove`)}</p>
      </div>
    );
  };

  const PromoCodeButton: FC<CouponDetailsInterface> = ({ code }): JSX.Element | null => {
    if (code === appliedPromoCode) {
      return <RemoveButton />;
    }
    return <AddButton />;
  };

  const onLogin = (): void => {
    if (isLoggedIn) return;
    if (isMobileApp) {
      return openModal({ id: Modals.Login });
    }
    toggleLoginModal({
      openModal: true,
      onCloseRedirect: () => null,
    });
  };

  const _applyPromoCode = (code: string): void => {
    if (!isLoggedIn) {
      return onLogin();
    }
    applyPromoCode(code);
  };

  const googleDeal = {
    data: googlePromotion,
    render: () => (
      <DiscountCard
        title={t(`deals.${DEAL_TYPES.GOOGLE_REVIEW_PROGRAM}.title`, {
          reward: googlePromotion.reward,
        })}
        description={
          <div className="flex">
            <p className="text-xs text-brand-text-secondary my-auto">
              {t(`deals.${DEAL_TYPES.GOOGLE_REVIEW_PROGRAM}.description`)}
            </p>
            <div className="p-2xs shadow-icon dark:shadow-none ml-2xs my-auto rounded-full">
              <GoogleIcon className="h-4 w-4" />
            </div>
          </div>
        }
        footer={<AddButton />}
        className={classnames(dealClassName, 'cursor-pointer')}
        onClick={onGoogleReviewProgramClick}
      />
    ),
  };

  const promoCodeDeals = collect(company?.promo_codes)
    .map((coupon) => ({
      data: coupon,
      render: () => (
        <DiscountCard
          key={coupon.code}
          title={t('discounts.title', { discount: getDiscount(coupon) })}
          description={getPromoCodeDescription(coupon)}
          footer={<PromoCodeButton {...coupon} />}
          onClick={() => (coupon.code === appliedPromoCode ? removePromoCode() : _applyPromoCode(coupon.code))}
          className={classnames(dealClassName, 'cursor-pointer')}
        />
      ),
    }))
    .toArray();

  const smartPricing = collect(company?.smart_pricing_deals)
    .map((item) => ({
      data: item,
      render: () => (
        <DiscountCard
          title={item.name}
          description={t(`deals.${DEAL_TYPES.SMART_PRICING_RULE}.description`)}
          className={dealClassName}
        />
      ),
    }))
    .toArray();

  const items = useMemo<Item[]>(
    () =>
      collect()
        .when(company?.show_master_promo_code, (c) => c.merge(promoCodeDeals))
        .when(hasGooglePromotion && !hasUploadedGoogleReview, (c) => c.push(googleDeal))
        .merge(smartPricing)
        .toArray(),
    [
      company?.show_master_promo_code,
      company?.smart_pricing_deals,
      company?.promo_codes,
      hasGooglePromotion,
      hasUploadedGoogleReview,
      googlePromotion,
      appliedPromoCode,
      showLoginPrompt,
      isLoggedIn,
      dealClassName,
    ],
  );

  return (
    items.length > 0 && (
      <div className={classnames('relative w-full', className)}>
        <CustomCarousel
          items={items}
          itemsPerSlide={isDesktop ? 2.5 : 1.1}
          slideJump={isDesktop ? 3 : 2}
          autoHeight
          arrowsPosition="top"
          arrowsClassName="lg:mr-lg mr-md"
          itemsClassName={classnames('lg:pl-none pl-md', itemsClassName)}
          title={
            <h5
              className={classnames('lg:ml-none ml-md font-semibold', {
                'text-md': isMobileApp,
              })}
            >
              {t('title')}
            </h5>
          }
          headerClassName="mb-[6px]"
        />
      </div>
    )
  );
};

export default DealsCarousel;
